import { children, createUniqueId, Show, splitProps } from 'solid-js';
import { cn, tv } from 'tailwind-variants';
import type { ParentProps, JSX } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Props = Omit<JSX.SvgSVGAttributes<SVGSVGElement>, 'class'> &
	VariantProps<typeof styles> &
	ParentProps<{
		layout?: 'vertical' | 'horizontal';
	}>;

export function ActivityIndicator(props: Props) {
	const id = createUniqueId();
	const [rest, spread] = splitProps(props, ['viewBox', 'layout', 'aria-label', 'action', 'size']);
	const label = children(() => props.children);

	return (
		<div
			role="progressbar"
			class={cn('flex items-center gap-x-2 gap-y-4', props.layout === 'horizontal' ? '' : 'flex-col')}
			aria-label={rest['aria-label']}
			aria-labelledby={label() ? id : undefined}
		>
			<svg viewBox="0 0 50 50" class={styles({ action: props.action, size: props.size })} {...spread}>
				<circle
					cx="25"
					cy="25"
					r="20"
					fill="none"
					class="animate-stroke stroke-current"
					stroke-width={8}
					stroke-linecap="round"
				/>
			</svg>
			<Show when={label()}>
				<div id={id} class="whitespace-nowrap">
					{label()}
				</div>
			</Show>
		</div>
	);
}

const styles = tv({
	base: 'size-6 shrink-0 grow-0 animate-spin',
	variants: {
		action: {
			primary: 'text-yellow-600 dark:text-yellow-400',
			neutral: 'text-teal-600 dark:text-teal-400',
			danger: 'text-rose-600',
		},
		size: {
			sm: 'size-4',
			md: 'size-8',
			lg: 'size-16',
		},
	},
	defaultVariants: {
		action: 'primary',
		size: 'md',
	},
});
